import { useState, useRef, useEffect }  from 'react';

import { Typography, Tooltip, IconButton } from '@material-ui/core'
import ReactCardFlip from 'react-card-flip';

import CompareArrowsIcon from '@material-ui/icons/CompareArrows';

import { ThemeColors } from '../Theme'

import { sampleData, evaluate } from './PassField';
import { DateUtils, ColorUtils } from 'react-frontend-utils';


const MAX_LENGTH_SECONDARY_PORTRAIT = 18;
const MAX_LENGTH_SECONDARY_LANDSCAPE = 26;

//ISO/IEC 7810 standard card dimensions, mm
const WIDTH = 85.60;
const HEIGHT =  53.98;
const CARD_ASPECT_RATIO = WIDTH/HEIGHT;  //width to height

const OUTER_PADDING = 0.06;  

//Height of the header, as a ratio to the full card height
const HEADER_PORTRAIT_HEIGHT_RATIO = 0.15;   
const HEADER_LANDSCAPE_HEIGHT_RATIO = 0.18;   

//Height of the barcode, as a ratio to the full card height
const BARCODE_PORTRAIT_HEIGHT_RATIO = 0.15;
const BARCODE_LANDSCAPE_HEIGHT_RATIO = 0.22;

/**
 * Preview component for an Physical Pass, front and back sides.  Provides an animation to flip between front and back sides.
 * Styling and relative dimensions based on the zeld designer template and ISO standard card dimensions.
 * 
 * This is display only - no changes to the pass are made in this component
 * 
 * Caller must pass props:
 *  
 *  previewWidth: desired render width, the height is scaled based on the aspect ratio of the pass
 *  maxFrontFields: maximum number of front fields allowed
 *  portraitBarcodeOnBack: if the barcode in portrait mode should be put on the back of the card
 *  pass field props: logoImage, extendedName, orientationIsVertical, secondaryFields, primaryField, enablePatronImage,
 *                    patronImageBorder, barcode, barcodeShowValue, backFields, titleFontSize, fontColor, titleFont
 *  sampleTestName: a sample name to show in the preview
 *  passImage: a sample patron image to show in the preview if enabled, generic passes can use this for logo, if not set
 *               use the default sample patron image
 *  frontOnly: if true, only show the front of the pass and hide the flip button
 */
export function PPassPreview(props) {

    const [isFlipped, setFlipped] = useState(false); //front is not flipped, back is flipped

    let width = props.previewWidth;

    let fontScale = props.previewWidth / 550;    //nominally, a scale of 1 is represented when the width is 300 pixels, otherwise scale up or down

    let height = props.previewWidth / CARD_ASPECT_RATIO;   //height scales with width 

    if (props.orientationIsVertical) {  //swap if portrait 
        width = height;
        height = props.previewWidth;
    }

    const reducedFrontFields = props.secondaryFields.slice(0, props.maxFrontFields);  //limit to the amount allowed


    //Outer pass style for front and back
    const passStyle = {
        height: height, 
        width: width, 
        border: '1px solid gray',
        borderRadius: 0.05 * props.previewWidth,       //scale as size grows
        backgroundColor: 'white',
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 4px 8px 0 rgba(0, 0, 0, 0.19)'
    }


    //Padding around edge
    const contentStyle = {
        padding: OUTER_PADDING * (height > width ? width : height)    //take smaller
    }

    //The total height, less the outer padding, plus 1 additional padding to seperate header from landscape/portrait container
    const heightLessPadding = height - 3*contentStyle.padding;
    const widthLessPadding = width - 2*contentStyle.padding;

    const headerHeightRatio = props.orientationIsVertical ? HEADER_PORTRAIT_HEIGHT_RATIO : HEADER_LANDSCAPE_HEIGHT_RATIO;

    const landscapePortraitHeight = heightLessPadding * (1 - headerHeightRatio);  //remaining space for container

    //Content of the header is at the top of the front of pass
    const headerContainerStyle = {
        height: heightLessPadding * headerHeightRatio, 
        width: '100%', 
        display: 'flex',
        alignItems: 'center',
        gap: '2%',
        marginBottom: contentStyle.padding,  //add the same padding to the bottom to seperate header
    }

    //Style for the section below the header, in landscape mode
    const landscapeContainerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        height: landscapePortraitHeight
    }

    //Style for the section below the header, in portrait mode
    const portraitContainerStyle = {
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        height: landscapePortraitHeight
    }

    //Back container 
    const backContainerStyle = {
        ...contentStyle, 
        height: height - 2*contentStyle.padding, //all space for container
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'space-between'
    }
    
    //Scaled text for values, where 1 is the scale of the Logo Title
    const valueText = (text, scale = 1, bold = false, align = 'left', color = 'black', italic = false) => {
        return (
            <div style={{color: color, fontWeight: bold ? 'bold' : 'normal', textAlign: align, fontSize: scale * 32 * fontScale, fontStyle: italic ? 'italic' : 'normal'}}>
                {text}
            </div>);
    }

    //Text for labels above a value
    const labelText = (text, align = 'left') => {
        return <div style={{fontWeight: 'bold', textAlign: align, fontSize: 20 * fontScale}}>{text}</div>
    }

    //Dynamically changing text component that resizes the font to fit the width
    const Name = (props) => {
        const baseFontSize = props.scale * 32 * fontScale;
        const [fontSize, setFontSize] = useState(baseFontSize);
        const elementRef = useRef(null);
        const MIN_FONT_SIZE = 4;

        useEffect(() => {
            if (!elementRef.current)
                return;

            elementRef.current.style.fontSize = `${baseFontSize}px`;
            const measuredWidth = elementRef.current.scrollWidth;

            let nextFontSize = baseFontSize;

            if (measuredWidth > props.containerWidth && measuredWidth > 0) {
                // Scale directly to the available width so text fills the space better.
                nextFontSize = Math.max(MIN_FONT_SIZE, baseFontSize * (props.containerWidth / measuredWidth));
                elementRef.current.style.fontSize = `${nextFontSize}px`;

                // Guard against rounding/layout edge cases that can still slightly overflow.
                if (elementRef.current.scrollWidth > props.containerWidth)
                    nextFontSize = Math.max(MIN_FONT_SIZE, nextFontSize * 0.98);
            }

            setFontSize(prev => (Math.abs(prev - nextFontSize) < 0.1 ? prev : nextFontSize));

          }, [baseFontSize, props.containerWidth, props.text]);
        

        return (
            <div ref={elementRef} style={{display: 'block', fontSize: fontSize, whiteSpace: 'nowrap'}}>
                {props.text}
            </div>);
    }

    //The colored box sits just below the header
    const primaryField = (width = '100%') => {

        if (!props.primaryField)  //not enabled, skip
            return null;

        let text = props.primaryField.defaultValue;
        let color = props.primaryField.defaultColor;
        let logicErrored = false;

        for (let colorLogic of props.primaryField.colorLogic) { //evaluate the color logic, if any

            const result = evaluate(colorLogic.dynamicDataType, colorLogic.operator, colorLogic.operand);
            if (result) {
                text = colorLogic.resultWhenTrue;
                color = colorLogic.colorWhenTrue;
                break;
            }
            if (result === null) {
                logicErrored = true;
            }
            
        }
  
        const fontColor = ColorUtils.fontColorForBackground(color);

        if (logicErrored)
            text = "Logic Error!";

        if (text)
            text = text.replace("{{year}}", DateUtils.currentYear());  //replace year with current year

        return (
            <div style={{border: logicErrored ? '2px solid red' : 'none'}}>
                <div style={{backgroundColor: logicErrored ? 'white' : color, width: width, padding: '2%', marginBottom: '2%'}}>
                    {valueText(text, 0.8, false, 'center', logicErrored ? 'red' : fontColor, logicErrored)}
                </div>
            </div>
        );
    }

    //Secondary fields with label to the left and text to the right
    const secondaryField = (field, index, centered = false, maxChars = Number.MAX_VALUE) => {

        let text = sampleData(field.dynamicDataType, field.fixedText);
        if (text && text.length > maxChars)
            text = text.slice(0, maxChars);

        return (
            <div key={index} style={{width: '100%', display: 'flex', justifyContent: centered ? 'center' : 'flex-start', alignItems: 'flex-end', gap: '5%'}}>
                {field.label ? labelText(field.label) : null}
                {valueText(text, 0.6)}
            </div>
        );
    }

    //Secondary fields with label above and text below
    const stackedSecondaryField = (field, index, spacing) => {
        return (
            <div key={index} style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '2%', marginBottom: spacing}}>
                {field.label ? labelText(field.label) : null}
                {valueText(sampleData(field.dynamicDataType, field.fixedText), 0.6)}
            </div>
        );
    }

    const patronImage = (width) => {
        if (props.enablePatronImage) {
            const imageSrc = props.passImage ? props.passImage : "samplePatron.jpg";
            return <img src={imageSrc} alt="cardImage" width={width} style={{border: props.patronImageBorder ? '2px solid gray' : 'none'}}/>;
        }
        else
            return null;
    }

    const barcode = (width) => {
        return props.barcode ? 
            <div style={{marginTop: '2%'}}>
                <img src="sampleCode128Barcode.jpg" alt="barcodeImage" style={{width: width ? width : widthLessPadding, height: height * (props.orientationIsVertical ? BARCODE_PORTRAIT_HEIGHT_RATIO : BARCODE_LANDSCAPE_HEIGHT_RATIO)}}/>
                {props.barcodeShowValue ? valueText("123456", 0.7, false, 'center', 'black') : null}
            </div>
            : null;
    }


    //Similar to stacked secondary fields, but with margin added
    const backField = (field, index) => {

        return (
            <div key={index} style={{width: '100%', marginBottom: props.orientationIsVertical ? '10%' : '5%'}}>
                {field.label ? labelText(field.label) : null}
                {valueText(sampleData(field.dynamicDataType, field.fixedText), 0.6, false, 'left')}
            </div>
        );
    }

    const titleFontScale = 1 + (props.titleFontSize * 0.2);

    
 
    return (
        <div>
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 10}}>
                <Typography variant='h6' align='center' style={{color: 'gray', fontStyle: 'italic', marginRight: 10}}>{"Pass Preview: " + (isFlipped ? "Back" : "Front")}</Typography>
                {props.frontOnly ? null :
                    <Tooltip title={"Flip to view " + (isFlipped ? "front" : "back") + " of pass"}>
                        <IconButton onClick={() => setFlipped(!isFlipped)}>
                            <CompareArrowsIcon fontSize='large' style={{color: ThemeColors.appBarBackground}}/>
                        </IconButton>
                    </Tooltip>
                }
            </div>

            <ReactCardFlip isFlipped={isFlipped}>

                {/*--------------------------- FRONT OF PASS ------------------------------*/}                 
                <div style={passStyle}> 
                    <div style={contentStyle}>

                        <div style={headerContainerStyle}>  {/* ---- HEADER ------ */}
                            {props.logoImage ?
                                <img src={props.logoImage} alt="logo" width={headerContainerStyle.height} height={headerContainerStyle.height}/>
                                : null
                            }
                            <div style={{width: '100%', color: props.fontColor, textAlign: props.logoImage ? 'left' : 'center', 
                                        fontWeight: 'bold', fontFamily: props.titleFont,
                                        fontSize: titleFontScale * 32 * fontScale}}>
                                {props.extendedName}
                            </div>
                        </div>

                
                        {props.orientationIsVertical ? //------------------ PORTRAIT --------------
                            
                            <div style={portraitContainerStyle}>
                                <div style={{width: '100%'}}>
                                    {primaryField('96%') /*account for 2% padding*/}
                                </div>
                                {patronImage('72%')}
                                <Name text={props.sampleTestName} scale={1.4} containerWidth={widthLessPadding}/>
                                
                                {props.secondaryFields.length > 0 || props.enablePatronImage || props.barcode ? null : <div style={{height: 1}}/>  /*empty space if needed*/}                          
                                
                                {props.enablePatronImage ?  //With image, use a regular secondary, otherwise stack them               
                                    reducedFrontFields.map((field, index) => secondaryField(field, index, true, MAX_LENGTH_SECONDARY_PORTRAIT))
                                    :
                                    reducedFrontFields.map((field, index) => stackedSecondaryField(field, index))
                                }
                                {props.portraitBarcodeOnBack ? null : barcode()}
                            </div>

                            :  //------------------ LANDSCAPE ----------------

                            <div style={landscapeContainerStyle}>

                                {/* ------ LEFT SECTION ------ */}
                                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '65%'}}>  
                                    {primaryField('96%') /*account for 2% padding*/}
                                    <Name text={props.sampleTestName} scale={1.4} containerWidth={widthLessPadding * .65}/>
                                    {(props.secondaryFields.length > 0 && props.enablePatronImage) || props.barcode ? null : <div style={{height: 1}}/>  /*empty space if needed*/}                                
                                    {props.enablePatronImage ? (
                                            props.barcode ? reducedFrontFields.map((field, index) => secondaryField(field, index, false, MAX_LENGTH_SECONDARY_LANDSCAPE)) :
                                                            reducedFrontFields.map((field, index) => stackedSecondaryField(field, index))
                                        ) : null 
                                    }
                                    {barcode(widthLessPadding * .65)}
                                </div>

                                {/* ------ RIGHT SECTION ------ */}
                                {props.enablePatronImage ?
                                    <div style={{display: 'flex', alignItems: 'flex-start', width: '30%'}}>
                                        {patronImage('100%')}
                                    </div>
                                    : 
                                    <div style={{display: 'flex', flexDirection: 'column', width: '30%'}}>
                                        {reducedFrontFields.map((field, index) => stackedSecondaryField(field, index, '15%'))}
                                    </div>
                                }
                            </div>
                        }

                    </div>
                </div>

                {/*--------------------------- BACK OF PASS ------------------------------*/}
                <div style={passStyle}> 
                    <div style={backContainerStyle}>
                        <div style={{height: '100%'}}>
                            {props.backFields.map((field, index) => backField(field, index))}
                        </div>
                        {props.backFields.length > 0 ? null : <div style={{height: 1}}/> /*empty space if needed*/}
                        {props.portraitBarcodeOnBack ? barcode() : null}
                    </div>
                </div>


            </ReactCardFlip>

        </div>
    );

}

